import { useEffect, useState } from 'react'
import { CircleMarker, Marker, Polyline, Tooltip } from 'react-leaflet'
import { Card, Button, usePollen } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import L from 'leaflet'
import { Crosshair } from 'lucide-react'
import { type LivebildInfo } from './Livebild'
import { liveDrohneSvg } from './taktischeSymbolUi'

// Live-Telemetrie der per DJI Pilot 2 (Cloud API) verbundenen Drohnen für die Taktik-Karte (Co-Pilot-Modus).
// dji-sync schreibt die MQTT-Telemetrie nach dji_live / flug_telemetrie; Abruf (auch /dji/flugrouten) über die Pilot-API.
// Pilot-/Co-Pilot-Zuordnung erfolgt im Pilot-Modul über die Meldung "Pilot zuordnen" (DjiZuordnung).

export interface LiveDrohne {
  sn: string
  in_der_luft: boolean
  alter_s: number
  drohne: string
  kennzeichen: string | null
  fernsteuerung: string | null
  daten: {
    lat?: number; lon?: number; hoehe_m?: number; speed_h?: number; kurs?: number; home_distanz_m?: number
    akku_prozent?: number; restflugzeit_s?: number; rth_akku_prozent?: number; modus?: string; gps_satelliten?: number
    wind_geschaetzt_ms?: number | null; wind_richtung_grad?: number | null
  }
  flug: { id: number; flug_nr: string | null; startzeit: string | null; pilot: string | null; co_pilot: string | null } | null
  livebild?: LivebildInfo
  /** Offene HMS-Meldungen der Drohne ab Stufe „Achtung“ (1) bzw. „Warnung“ (2) */
  hms?: { code: string; stufe: number; meldung: string | null }[]
}


const zahl = (v: number | undefined | null, stellen = 0) =>
  v === undefined || v === null ? '–' : v.toLocaleString('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: stellen })

/** Route eines Fluges für die Ebene „Flugrouten“ (laufender Flug oder kürzlich gelandet) */
export interface Flugroute {
  flug_id: number
  flug_nr: string | null
  sn: string
  /** Flug läuft noch */
  aktiv: boolean
  drohne: string
  startzeit: string | null
  endzeit: string | null
  punkte: [number, number][]
}

/** Drohnen in der Luft alle 2 s, Flugrouten alle 10 s. */
export function useDrohnenLive() {
  const [drohnen, setDrohnen] = useState<LiveDrohne[]>([])
  const [routen, setRouten] = useState<Flugroute[]>([])

  const laden = usePollen('Live-Drohnen', lebt => api.get('/dji/live')
    .then(r => { if (lebt()) setDrohnen((r.data as LiveDrohne[]).filter(d => d.in_der_luft)) }), 2000, [])

  // Neue Flug-ID (Start) sofort nachladen, damit die alte Route nicht bis zu 10 s stehen bleibt
  const flugIds = drohnen.map(d => d.flug?.id ?? '').join(',')
  usePollen('Flugrouten', lebt => api.get('/dji/flugrouten').then(r => { if (lebt()) setRouten(r.data as Flugroute[]) }), 10000, [flugIds])

  return { drohnen, routen, neuLaden: laden }
}

function icon(d: LiveDrohne) {
  const veraltet = d.alter_s > 10
  return L.divIcon({
    className: '',
    iconSize: [80, 80],
    iconAnchor: [40, 40],
    html: `<div style="width:80px;height:80px;opacity:${veraltet ? 0.5 : 1};filter:drop-shadow(0 1px 2px rgba(0,0,0,.35))">${liveDrohneSvg(d.daten.kurs)}</div>`,
  })
}

const uhr = (s: string | null) => (s ? new Date(s).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '–')

/**
 * Ebene „Flugrouten“ (innerhalb von MapContainer): bisheriger Weg des laufenden Fluges je Drohne, bis zur
 * aktuellen Position. Nach der Landung bleibt die Route gestrichelt stehen, bis die Drohne wieder startet
 * (dann ersetzt der neue Flug sie) – höchstens eine Stunde (FLUGROUTE_NACH_LANDUNG_MIN in der API).
 */
export function FlugroutenLayer({ routen, drohnen }: { routen: Flugroute[]; drohnen: LiveDrohne[] }) {
  return (
    <>
      {routen.map(r => {
        const live = drohnen.find(d => d.sn === r.sn)
        // Drohne ist bereits zu einem neuen Flug gestartet: alte Route sofort ausblenden
        if (live?.flug && live.flug.id !== r.flug_id) return null
        const punkte = live?.flug?.id === r.flug_id && live.daten.lat && live.daten.lon
          ? [...r.punkte, [live.daten.lat, live.daten.lon] as [number, number]] : r.punkte
        if (!punkte.length) return null
        const text = `Flugroute ${r.drohne}${r.flug_nr ? ` · ${r.flug_nr}` : ''} · Start ${uhr(r.startzeit)}${r.aktiv ? '' : ` · gelandet ${uhr(r.endzeit)}`}`
        return (
          <span key={r.flug_id}>
            {punkte.length > 1 && <>
              <Polyline positions={punkte} pathOptions={{ color: '#ffffff', weight: 6, opacity: r.aktiv ? 0.85 : 0.6 }} />
              <Polyline positions={punkte} pathOptions={{ color: '#d9282f', weight: 3, opacity: r.aktiv ? 0.95 : 0.7, dashArray: r.aktiv ? undefined : '6 6' }}>
                <Tooltip sticky>{text}</Tooltip>
              </Polyline>
            </>}
            <CircleMarker center={punkte[0]} radius={5} pathOptions={{ color: '#d9282f', weight: 2, fillColor: '#ffffff', fillOpacity: 1 }}>
              <Tooltip>{`Start ${uhr(r.startzeit)} · ${r.drohne}`}</Tooltip>
            </CircleMarker>
          </span>
        )
      })}
    </>
  )
}

/** Kartenebene Drohnen (innerhalb von MapContainer). */
export function DrohnenLayer({ drohnen }: { drohnen: LiveDrohne[] }) {
  return (
    <>
      {drohnen.map(d => d.daten.lat && d.daten.lon && (
        <Marker key={d.sn} position={[d.daten.lat, d.daten.lon]} icon={icon(d)} zIndexOffset={1000}>
          <Tooltip direction="top" offset={[0, -32]} permanent>
            <span className="text-xs">
              <b>{d.kennzeichen || d.drohne}</b> · {zahl(d.daten.hoehe_m)} m · {zahl(d.daten.akku_prozent)} %
            </span>
          </Tooltip>
        </Marker>
      ))}
    </>
  )
}

/** Seitenleiste: Werte je Drohne, Pilot/Co-Pilot und Zentrieren. */
export function DrohnenPanel({ drohnen, onZentrieren }: {
  drohnen: LiveDrohne[]
  onZentrieren: (lat: number, lon: number) => void
}) {

  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-dark-text">Drohnen live</span>
        <span className="text-xs text-dark-text-subtle">{drohnen.length ? `${drohnen.length} in der Luft` : 'keine in der Luft'}</span>
      </div>
      {!drohnen.length ? (
        <p className="text-xs text-dark-text-subtle">Über DJI Pilot 2 verbundene Drohnen erscheinen hier und auf der Karte, sobald sie abheben.</p>
      ) : (
        <div className="space-y-3">
          {drohnen.map(d => (
            <div key={d.sn} className="rounded-lg border border-dark-border bg-dark-bg-tertiary/50 p-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-dark-text">{d.kennzeichen || d.drohne}</div>
                  <div className="truncate text-[11px] text-dark-text-muted">{d.daten.modus || '–'}{d.flug?.flug_nr ? ` · ${d.flug.flug_nr}` : ''}{d.alter_s > 10 ? ` · keine Daten seit ${Math.round(d.alter_s)} s` : ''}</div>
                  {(d.hms ?? []).map(h => (
                    <div key={h.code} className={`mt-0.5 text-[11px] ${h.stufe >= 2 ? 'text-red-300' : 'text-amber-300'}`}>
                      {h.stufe >= 2 ? 'Warnung' : 'Achtung'}: {h.meldung || `HMS ${h.code}`}
                    </div>
                  ))}
                  {!(d.daten.lat && d.daten.lon) && (
                    <div className="mt-0.5 text-[11px] text-amber-300">Keine Position von der Drohne (kein GPS-Fix) – nicht auf der Karte</div>
                  )}
                </div>
                {d.daten.lat && d.daten.lon && (
                  <Button size="sm" variant="secondary" className="min-h-[48px] min-w-[48px]" title="Auf Karte zentrieren" onClick={() => onZentrieren(d.daten.lat!, d.daten.lon!)}>
                    <Crosshair className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-1 text-center text-[11px]">
                <div><div className="text-dark-text-subtle">Höhe</div><div className="font-semibold text-dark-text">{zahl(d.daten.hoehe_m)} m</div></div>
                <div><div className="text-dark-text-subtle">Tempo</div><div className="font-semibold text-dark-text">{zahl(d.daten.speed_h !== undefined ? d.daten.speed_h * 3.6 : undefined)} km/h</div></div>
                <div><div className="text-dark-text-subtle">Distanz</div><div className="font-semibold text-dark-text">{zahl(d.daten.home_distanz_m)} m</div></div>
                <div><div className="text-dark-text-subtle">Akku</div>
                  <div className={`font-semibold ${(d.daten.akku_prozent ?? 100) <= (d.daten.rth_akku_prozent ?? 25) + 5 ? 'text-red-300' : 'text-dark-text'}`}>{zahl(d.daten.akku_prozent)} %</div></div>
                <div><div className="text-dark-text-subtle">Rest</div><div className="font-semibold text-dark-text">{d.daten.restflugzeit_s !== undefined ? `${Math.floor(d.daten.restflugzeit_s / 60)} min` : '–'}</div></div>
                <div><div className="text-dark-text-subtle">GPS</div><div className="font-semibold text-dark-text">{zahl(d.daten.gps_satelliten)}</div></div>
                {d.daten.wind_geschaetzt_ms != null && (
                  <div className="col-span-3"><div className="text-dark-text-subtle">Wind (geschätzt)</div>
                    <div className="font-semibold text-dark-text">{zahl(d.daten.wind_geschaetzt_ms, 1)} m/s{d.daten.wind_richtung_grad != null ? ` · ${['N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'][Math.round(d.daten.wind_richtung_grad / 45) % 8]}` : ''}</div></div>
                )}
              </div>
              {d.flug && (
                <div className="mt-2 text-[11px] text-dark-text-muted">
                  Pilot: {d.flug.pilot || <span className="text-amber-300">offen</span>} · Co-Pilot: {d.flug.co_pilot || '–'}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
